/**
 * Social Network Sharing Utilities
 * 
 * Provides comprehensive sharing functionality for various social media platforms
 * following 2024 best practices with Web Share API integration and fallbacks.
 */

export interface ShareOptions {
  title: string;
  text: string;
  url: string;
  imageUrl?: string;
  formatLocationDisplay?: () => string;
}

export interface ToastFunction {
  (options: {
    title: string;
    description: string;
    variant?: 'default' | 'destructive';
  }): void;
}

/**
 * WhatsApp sharing via direct URL scheme
 * Simple and reliable method that works across all platforms
 */
export const shareToWhatsApp = (options: ShareOptions): void => {
  const { title, url } = options;
  const text = `${title}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${text} - ${url}`)}`;
  window.open(whatsappUrl, '_blank');
};

/**
 * Instagram sharing with Web Share API and URL scheme fallbacks
 * Follows 2024 best practices with proper error handling
 */
export const shareToInstagram = async (options: ShareOptions, toast: ToastFunction): Promise<void> => {
  const { title, text, url, imageUrl, formatLocationDisplay } = options;
  
  // Feature detection first - check if Web Share API is supported
  if (!navigator.share) {
    // No Web Share API support - go directly to URL scheme fallback
    const fallbackText = `${title} - ${url}`;
    window.location.href = `instagram://sharesheet?text=${encodeURIComponent(fallbackText)}`;
    return;
  }

  try {
    let shareData: { files?: File[]; title: string; text: string; url: string };

    if (imageUrl) {
      // Try to share with image
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.jpg`, { 
          type: 'image/jpeg' 
        });
        
        shareData = {
          files: [file],
          title,
          text: formatLocationDisplay ? `${text} - ${formatLocationDisplay()}` : text,
          url
        };

        // Check if sharing with files is supported
        if (!navigator.canShare(shareData)) {
          throw new Error('File sharing not supported');
        }
      } catch (imageError) {
        console.log('Image sharing not supported, falling back to text:', imageError);
        // Fallback to text-only sharing
        shareData = {
          title,
          text: formatLocationDisplay ? `${text} - ${formatLocationDisplay()}` : text,
          url
        };
      }
    } else {
      // No image available - use text sharing
      shareData = {
        title,
        text: formatLocationDisplay ? `${text} - ${formatLocationDisplay()}` : text,
        url
      };
    }

    // Final check if the share data is supported
    if (!navigator.canShare(shareData)) {
      throw new Error('Share data not supported');
    }

    // Attempt to share
    await navigator.share(shareData);
    console.log('Instagram content shared successfully');

  } catch (error: any) {
    // Handle different error types properly
    if (error.name === 'AbortError') {
      // User cancelled the share - this is normal behavior, do nothing
      console.log('Instagram share cancelled by user');
      return;
    }

    if (error.name === 'NotAllowedError') {
      // Permission denied or no transient activation
      console.log('Instagram share not allowed:', error);
      toast({
        title: "Sharing not available",
        description: "Please try again or copy the link manually.",
      });
      return;
    }

    // For other errors (like DataError, network issues, etc.), try URL scheme fallback
    console.log('Instagram share failed, trying URL scheme fallback:', error);
    try {
      const fallbackText = `${title} - ${url}`;
      window.location.href = `instagram://sharesheet?text=${encodeURIComponent(fallbackText)}`;
    } catch (fallbackError) {
      // Final fallback - copy to clipboard
      console.log('Instagram URL scheme failed, copying to clipboard:', fallbackError);
      navigator.clipboard?.writeText(`${title} - ${url}`)
        .then(() => {
          toast({
            title: "Link copied!",
            description: "Link copied to clipboard. You can paste it in Instagram.",
          });
        })
        .catch(() => {
          toast({
            variant: "destructive",
            title: "Sharing not available",
            description: "Please copy the URL manually to share on Instagram.",
          });
        });
    }
  }
};

/**
 * Facebook sharing with Web Share API and popup fallbacks
 * Follows 2024 best practices with centered popup positioning
 */
export const shareToFacebook = async (options: ShareOptions, toast: ToastFunction): Promise<void> => {
  const { title, text, url, formatLocationDisplay } = options;
  
  // Feature detection first - check if Web Share API is supported
  if (navigator.share) {
    try {
      // Try Web Share API first (mobile-first approach)
      const shareData = {
        title,
        text: formatLocationDisplay ? `${text} - ${formatLocationDisplay()}` : text,
        url
      };

      // Check if sharing is supported
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        console.log('Facebook sharing via Web Share API successful');
        return;
      }
    } catch (error: any) {
      // Handle different error types properly
      if (error.name === 'AbortError') {
        // User cancelled the share - this is normal behavior, do nothing
        console.log('Facebook share cancelled by user');
        return;
      }

      if (error.name === 'NotAllowedError') {
        // Permission denied or no transient activation
        console.log('Facebook share not allowed:', error);
        toast({
          title: "Sharing not available",
          description: "Please try again or use the popup option.",
        });
        // Fall through to popup fallback
      } else {
        console.log('Facebook Web Share API failed, using popup fallback:', error);
        // Fall through to popup fallback
      }
    }
  }

  // Fallback to Facebook popup (2024 best practices)
  try {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`;
    
    // Calculate centered position (2024 best practices)
    const width = 550;
    const height = 450;
    const leftPosition = (window.screen.width / 2) - ((width / 2) + 10);
    const topPosition = (window.screen.height / 2) - ((height / 2) + 50);
    
    // Optimal window features for 2024
    const windowFeatures = `status=no,height=${height},width=${width},resizable=yes,left=${leftPosition},top=${topPosition},toolbar=no,menubar=no,scrollbars=no,location=no,directories=no`;
    
    const popup = window.open(facebookUrl, 'facebook_share', windowFeatures);
    
    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      throw new Error('Popup blocked');
    }
    
    console.log('Facebook popup sharing opened successfully');
    
  } catch (popupError) {
    console.log('Facebook popup sharing failed, copying to clipboard:', popupError);
    // Final fallback - copy to clipboard
    navigator.clipboard?.writeText(`${title} - ${url}`)
      .then(() => {
        toast({
          title: "Link copied!",
          description: "Link copied to clipboard. You can paste it on Facebook.",
        });
      })
      .catch(() => {
        toast({
          variant: "destructive",
          title: "Sharing not available",
          description: "Please copy the URL manually to share on Facebook.",
        });
      });
  }
};

/**
 * Generic social sharing function that handles multiple platforms
 */
export const shareToSocialNetwork = async (
  platform: 'whatsapp' | 'instagram' | 'facebook',
  options: ShareOptions,
  toast: ToastFunction
): Promise<void> => {
  switch (platform) {
    case 'whatsapp':
      shareToWhatsApp(options);
      break;
    case 'instagram':
      await shareToInstagram(options, toast);
      break;
    case 'facebook':
      await shareToFacebook(options, toast);
      break;
    default:
      console.error(`Unsupported social network platform: ${platform}`);
  }
};

/**
 * Helper function to create share options from common parameters
 */
export const createShareOptions = (
  title: string,
  url: string = window.location.href,
  imageUrl?: string,
  formatLocationDisplay?: () => string
): ShareOptions => ({
  title: `Check out ${title}`,
  text: `Discover ${title}`,
  url,
  imageUrl,
  formatLocationDisplay
});